import { useEffect, useLayoutEffect, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import Confetti from 'react-confetti';
import { AnimatePresence, motion } from 'motion/react';
import { AlertCircle, Check, Clock, Loader2, MessageCircleQuestion } from 'lucide-react';
import type { AimdocInputField, AimdocJobQuestion } from '@aimdoc/sdk';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { AimdocSymbol } from '../brand/AimdocLogo';
import { ONBOARDING_EASE, Panel } from './OnboardingStage';
import type { RunProgressItem, RunSummary } from './types';

/** Only show work the running AI actually planned. */
export function deriveProgress(run: RunSummary | null): RunProgressItem[] {
  return run?.progress?.length ? run.progress : [];
}

export function isRunSettled(run: RunSummary | null) {
  return !run || run.status === 'completed' || run.status === 'failed' || run.status === 'cancelled';
}

/** The answer the build screen posts to POST /sdk/v1/jobs/{id}/answer. */
export interface BuildAnswer {
  answers?: Record<string, unknown>;
  text?: string;
}

/** SSR-safe window size for the celebration canvas. */
function useViewportSize() {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const update = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return size;
}

function ElapsedTimer({ running }: { running: boolean }) {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => setSeconds((value) => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, [running]);
  const minutes = Math.floor(seconds / 60);
  const rest = String(seconds % 60).padStart(2, '0');
  return (
    <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground" aria-hidden>
      {minutes}:{rest}
    </span>
  );
}

/** State icon: a solid check that draws itself when done, a brand ring
 * spinner while active, a dashed ring while queued. The pop is a tween, not
 * a spring — springs overshoot past scale 1 and the list's clipped viewport
 * would shave the icon's left edge mid-animation. */
function ItemIcon({ state }: { state: RunProgressItem['state'] }) {
  if (state === 'done') {
    return (
      <motion.span
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.22, ease: ONBOARDING_EASE }}
        className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-emerald-500 text-white shadow-[0_4px_10px_-4px_rgba(16,185,129,0.8)]"
      >
        <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" aria-hidden>
          <motion.path
            d="M3.5 8.5 6.5 11.5 12.5 4.5"
            stroke="currentColor"
            strokeWidth={2.4}
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.32, delay: 0.08, ease: ONBOARDING_EASE }}
          />
        </svg>
      </motion.span>
    );
  }
  if (state === 'active') {
    return (
      <span className="relative flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[#1173F0]/10 dark:bg-[#3B8BFF]/15">
        <span className="absolute inset-0 animate-spin rounded-full border-2 border-[#1173F0]/15 border-t-[#1173F0] [animation-duration:0.9s] dark:border-[#3B8BFF]/20 dark:border-t-[#3B8BFF]" />
        <span className="h-1.5 w-1.5 rounded-full bg-[#1173F0] dark:bg-[#3B8BFF]" />
      </span>
    );
  }
  return <span className="block h-[22px] w-[22px] rounded-full border border-dashed border-muted-foreground/35 bg-background" />;
}

function ProgressItem({
  item,
  last,
  registerRef,
}: {
  item: RunProgressItem;
  last: boolean;
  registerRef: (key: string, node: HTMLDivElement | null) => void;
}) {
  return (
    <motion.div
      ref={(node) => registerRef(item.key, node)}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: ONBOARDING_EASE }}
      className="flex gap-3"
    >
      {/* Icon column carries the timeline rail down to the next row (through the list's gap). */}
      <div className="relative flex flex-col items-center pt-px">
        <ItemIcon state={item.state} />
        {!last && (
          <span
            aria-hidden
            className={cn(
              'absolute left-1/2 top-[25px] -bottom-3 w-px -translate-x-1/2 transition-colors duration-500',
              item.state === 'done' ? 'bg-emerald-500/40' : 'bg-border'
            )}
          />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            'text-sm leading-5 [overflow-wrap:anywhere]',
            item.state === 'active' ? 'aimdoc-text-shimmer font-semibold' : 'font-medium',
            item.state === 'todo' && 'text-muted-foreground/70'
          )}
        >
          {item.label}
        </div>
        {/* The active item's live detail ("Drafted: Weekly shipment audit") is
            what makes the build feel alive; done rows stay clean. Height is
            animated (not just opacity) so the line unfolds instead of
            shoving the rows below it down in one jump. */}
        <AnimatePresence mode="wait" initial={false}>
          {item.state === 'active' && item.detail && (
            <motion.div
              key={item.detail}
              initial={{ opacity: 0, height: 0, marginTop: 0 }}
              animate={{ opacity: 1, height: 'auto', marginTop: 2 }}
              exit={{ opacity: 0, height: 0, marginTop: 0 }}
              transition={{ duration: 0.26, ease: ONBOARDING_EASE }}
              className="overflow-hidden text-xs leading-4 text-muted-foreground [overflow-wrap:anywhere]"
            >
              {item.detail}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

/** Keeps the current row clear of the bottom gradient (h-8) and hints the
 * row after it. */
const FOLLOW_PEEK_PX = 36;

/**
 * The checklist never shows a scrollbar. It follows like a terminal: the
 * list stays top-aligned while it fits, so finished rows pile up on screen.
 * Only when the current item (first non-done) would sink under the bottom
 * edge does the list translate up — just enough to keep it in view — and
 * the oldest done rows slide out the top of the clipped viewport.
 *
 * The anchor lags the data by a beat: when a task completes its check mark
 * gets a moment on screen before any slide. The slide spring is overdamped
 * so it glides without overshooting.
 */
function ProgressList({ items }: { items: RunProgressItem[] }) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [offset, setOffset] = useState(0);

  const anchorIndex = items.findIndex((item) => item.state !== 'done');
  // -1 (all done) anchors past the end so the tail of the list stays in view.
  const targetAnchor = anchorIndex < 0 ? items.length : anchorIndex;
  const [displayAnchor, setDisplayAnchor] = useState(targetAnchor);

  useEffect(() => {
    if (targetAnchor === displayAnchor) return;
    // Retreats (a re-run resetting the plan) snap; advances dwell so the
    // completed item's check registers before it slides away.
    if (targetAnchor < displayAnchor) {
      setDisplayAnchor(targetAnchor);
      return;
    }
    const timer = window.setTimeout(() => setDisplayAnchor(targetAnchor), 950);
    return () => window.clearTimeout(timer);
  }, [targetAnchor, displayAnchor]);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const list = listRef.current;
    if (!viewport || !list) return;
    const measure = () => {
      const maxOffset = Math.max(0, list.scrollHeight - viewport.clientHeight);
      const anchor = displayAnchor < items.length ? itemRefs.current.get(items[displayAnchor]?.key) : undefined;
      if (!anchor) {
        // All done (or not yet measured): keep the tail of the list in view.
        setOffset(maxOffset);
        return;
      }
      // Slide only once the anchor row would fall off the bottom, and only
      // by what that row needs — history above stays visible until the room
      // is genuinely spoken for.
      const needed = anchor.offsetTop + anchor.offsetHeight + FOLLOW_PEEK_PX - viewport.clientHeight;
      setOffset(Math.min(Math.max(0, needed), maxOffset));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    observer.observe(list);
    return () => observer.disconnect();
  }, [items, displayAnchor]);

  const registerRef = (key: string, node: HTMLDivElement | null) => {
    if (node) itemRefs.current.set(key, node);
    else itemRefs.current.delete(key);
  };

  return (
    <div ref={viewportRef} className="relative min-h-0 flex-1 overflow-hidden">
      <motion.div
        ref={listRef}
        className="flex flex-col gap-3.5"
        initial={false}
        animate={{ y: -offset }}
        transition={{ type: 'spring', stiffness: 120, damping: 26 }}
      >
        {items.map((item, index) => (
          <ProgressItem key={item.key} item={item} last={index === items.length - 1} registerRef={registerRef} />
        ))}
      </motion.div>
      {/* Once the list is following, soften the top edge where done rows
          slide out; while everything still fits, leave the first row crisp. */}
      {offset > 0 && (
        <div className="pointer-events-none absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-card to-transparent" />
      )}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-card to-transparent" />
    </div>
  );
}

/** Placeholder rows while the running AI is still planning its checklist. */
function PlanSkeleton({ label }: { label: string }) {
  return (
    <div className="w-full" role="status">
      <div className="flex flex-col gap-3.5">
        {['w-3/4', 'w-2/3', 'w-1/2'].map((width, index) => (
          <div key={width} className="flex items-center gap-3">
            <span
              className="h-[22px] w-[22px] shrink-0 animate-pulse rounded-full border border-dashed border-muted-foreground/30"
              style={{ animationDelay: `${index * 160}ms` }}
            />
            <span
              className={cn('h-3 animate-pulse rounded-full bg-muted', width)}
              style={{ animationDelay: `${index * 160}ms` }}
            />
          </div>
        ))}
      </div>
      <p className="aimdoc-text-shimmer aimdoc-text-shimmer-muted mt-5 text-center text-xs font-medium">{label}</p>
    </div>
  );
}

type Tone = 'working' | 'waiting' | 'completed' | 'failed';

function toneOf(run: RunSummary | null): Tone {
  if (run?.status === 'completed') return 'completed';
  if (run?.status === 'failed' || run?.status === 'cancelled') return 'failed';
  if (run?.status === 'waiting') return 'waiting';
  return 'working';
}

const TONE_CLASSES: Record<Tone, { pill: string; icon: string; border: string; bar: string }> = {
  working: {
    pill: 'border-[#1173F0]/20 bg-[#1173F0]/[0.06] text-[#1173F0] dark:border-[#3B8BFF]/25 dark:text-[#6AA8FF]',
    icon: 'bg-[#1173F0] text-white',
    border: '',
    bar: 'bg-gradient-to-r from-[#1173F0]/60 to-[#1173F0]',
  },
  waiting: {
    pill: 'border-amber-500/25 bg-amber-500/10 text-amber-700 dark:text-amber-400',
    icon: 'bg-amber-500 text-white',
    border: 'border-amber-500/30',
    bar: 'bg-amber-500',
  },
  completed: {
    pill: 'border-emerald-500/25 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
    icon: 'bg-emerald-500 text-white',
    border: 'border-emerald-500/35',
    bar: 'bg-emerald-500',
  },
  failed: {
    pill: 'border-red-500/25 bg-red-500/10 text-red-600',
    icon: 'bg-red-500 text-white',
    border: 'border-red-500/30',
    bar: 'bg-blue-500',
  },
};

function StatusPill({ run }: { run: RunSummary | null }) {
  const tone = toneOf(run);
  return (
    <span className={cn('flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors duration-500', TONE_CLASSES[tone].pill)}>
      {tone === 'completed' ? (
        <Check className="h-3 w-3" />
      ) : tone === 'failed' ? (
        <AlertCircle className="h-3 w-3" />
      ) : tone === 'waiting' ? (
        <MessageCircleQuestion className="h-3 w-3" />
      ) : (
        <span className="relative flex h-1.5 w-1.5">
          {run && <span className="absolute inset-0 animate-ping rounded-full bg-current opacity-60" />}
          <span className="relative h-1.5 w-1.5 rounded-full bg-current" />
        </span>
      )}
      {tone === 'completed' ? 'Done' : tone === 'failed' ? 'Stopped' : tone === 'waiting' ? 'Needs you' : run ? 'Working' : 'Queued'}
    </span>
  );
}

const fieldLabel = (name: string, field: AimdocInputField) =>
  field.title || name.replace(/[_-]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

/**
 * The job asked the owner something mid-setup (which warehouse to start
 * with, which tone the agent should take). A compact form: fields from the
 * question's input_schema, radios from its options, otherwise free text.
 */
export function QuestionForm({
  question,
  busy,
  onAnswer,
}: {
  question: AimdocJobQuestion;
  busy: boolean;
  onAnswer: (answer: BuildAnswer) => void | Promise<void>;
}) {
  const fields = Object.entries(question.input_schema?.properties ?? {});
  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const initial: Record<string, unknown> = {};
    for (const [name, field] of fields) initial[name] = field.default ?? (field.type === 'boolean' ? false : '');
    return initial;
  });
  const [text, setText] = useState('');
  const setValue = (name: string, value: unknown) => setValues((current) => ({ ...current, [name]: value }));
  const canSubmit = fields.length > 0 || text.trim().length > 0;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit || busy) return;
    void onAnswer(fields.length ? { answers: values } : { text: text.trim() });
  };

  const inputClass =
    'h-10 w-full rounded-lg border border-border bg-background px-3 text-sm outline-none transition-[border-color,box-shadow] focus-visible:border-[#1173F0]/60 focus-visible:ring-4 focus-visible:ring-[#1173F0]/10';

  return (
    <motion.form
      onSubmit={submit}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: ONBOARDING_EASE }}
      className="flex shrink-0 flex-col gap-3 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] px-4 py-3.5"
    >
      <div className="flex items-start gap-2">
        <MessageCircleQuestion className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-5 [overflow-wrap:anywhere]">{question.question}</p>
          {question.context && <p className="mt-1 text-xs leading-4 text-muted-foreground [overflow-wrap:anywhere]">{question.context}</p>}
        </div>
      </div>
      {fields.length > 0 ? (
        <div className="flex flex-col gap-2.5">
          {fields.map(([name, field]) => {
            const id = `build-q-${question.id}-${name}`;
            const required = question.input_schema?.required?.includes(name);
            if (field.type === 'boolean') {
              return (
                <label key={name} htmlFor={id} className="flex items-center gap-2 text-sm">
                  <input id={id} type="checkbox" checked={Boolean(values[name])} onChange={(e) => setValue(name, e.target.checked)} />
                  {fieldLabel(name, field)}
                </label>
              );
            }
            return (
              <label key={name} htmlFor={id} className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
                {fieldLabel(name, field)}
                {field.enum ? (
                  <select id={id} className={inputClass} required={required} value={String(values[name] ?? '')} onChange={(e) => setValue(name, e.target.value)}>
                    <option value="">Choose…</option>
                    {field.enum.map((option) => (
                      <option key={String(option)} value={String(option)}>
                        {String(option)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id={id}
                    className={inputClass}
                    required={required}
                    type={field.type === 'number' || field.type === 'integer' ? 'number' : field.format === 'date' ? 'date' : 'text'}
                    placeholder={field.description ?? undefined}
                    value={String(values[name] ?? '')}
                    onChange={(e) =>
                      setValue(
                        name,
                        field.type === 'number' || field.type === 'integer'
                          ? e.target.value === '' ? '' : Number(e.target.value)
                          : e.target.value
                      )
                    }
                  />
                )}
              </label>
            );
          })}
        </div>
      ) : question.options?.length ? (
        <div className="flex flex-col gap-1.5" role="radiogroup">
          {question.options.map((option) => (
            <label
              key={option}
              className={cn(
                'flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2 text-sm transition-colors',
                text === option ? 'border-[#1173F0]/60 bg-background ring-4 ring-[#1173F0]/10' : 'border-border/70 bg-background/60 hover:border-[#1173F0]/30'
              )}
            >
              <input type="radio" className="accent-[#1173F0]" name={`build-q-${question.id}`} value={option} checked={text === option} onChange={() => setText(option)} />
              {option}
            </label>
          ))}
        </div>
      ) : (
        <textarea
          className="min-h-[72px] w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none transition-[border-color,box-shadow] focus-visible:border-[#1173F0]/60 focus-visible:ring-4 focus-visible:ring-[#1173F0]/10"
          placeholder="Type your answer"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      )}
      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px] leading-4 text-muted-foreground">
          {question.default_action ? `If you skip this: ${question.default_action}` : 'The build continues as soon as you answer.'}
        </span>
        <Button type="submit" size="sm" disabled={!canSubmit || busy} className="h-8 gap-1.5 rounded-full px-3.5">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          Send answer
        </Button>
      </div>
    </motion.form>
  );
}

/**
 * The job's face in the card header: the Aimdoc symbol on a brand tile with a
 * slow conic halo while it works; settles to a check, a question or a stop.
 */
function TrackOrb({ tone, live }: { tone: Tone; live: boolean }) {
  const working = tone === 'working';
  return (
    <span className="relative flex h-10 w-10 shrink-0 items-center justify-center">
      {working && live && (
        <span
          aria-hidden
          className="absolute -inset-[2px] overflow-hidden rounded-[14px]"
        >
          <span className="absolute -inset-[50%] animate-spin bg-[conic-gradient(from_0deg,transparent_0deg,transparent_250deg,#1173F0_340deg,transparent_360deg)] [animation-duration:2.4s]" />
        </span>
      )}
      <span
        className={cn(
          'relative flex h-10 w-10 items-center justify-center rounded-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.25)] transition-colors duration-500',
          TONE_CLASSES[tone].icon
        )}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={tone}
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.6, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            className="flex"
          >
            {tone === 'completed' ? (
              <Check className="h-5 w-5" strokeWidth={2.75} />
            ) : tone === 'failed' ? (
              <AlertCircle className="h-5 w-5" />
            ) : tone === 'waiting' ? (
              <MessageCircleQuestion className="h-5 w-5" />
            ) : (
              <AimdocSymbol mono className="h-[22px] w-[22px] text-white" aria-hidden />
            )}
          </motion.span>
        </AnimatePresence>
      </span>
    </span>
  );
}

function Track({
  title,
  subtitle,
  run,
  items,
  question,
  answering,
  onAnswer,
}: {
  title: string;
  subtitle: string;
  run: RunSummary | null;
  items: RunProgressItem[];
  question: AimdocJobQuestion | null;
  answering: boolean;
  onAnswer?: (answer: BuildAnswer) => void | Promise<void>;
}) {
  const tone = toneOf(run);
  const completed = tone === 'completed';
  const failed = tone === 'failed';
  const doneCount = items.filter((item) => item.state === 'done').length;
  return (
    <Panel className={cn('flex min-h-0 min-w-0 flex-col gap-4 transition-colors duration-500', TONE_CLASSES[tone].border)}>
      <div className="flex shrink-0 items-center gap-3">
        <TrackOrb tone={tone} live={Boolean(run)} />
        <div className="min-w-0 flex-1">
          <div className="text-[15px] font-semibold tracking-[-0.01em]">{title}</div>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={run?.headline || subtitle}
              initial={{ opacity: 0, y: 4, filter: 'blur(2px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, y: -4, filter: 'blur(2px)' }}
              transition={{ duration: 0.3, ease: ONBOARDING_EASE }}
              className="truncate text-xs text-muted-foreground"
            >
              {run?.headline || subtitle}
            </motion.div>
          </AnimatePresence>
        </div>
        <StatusPill run={run} />
      </div>

      {items.length > 0 && (
        <div className="flex shrink-0 items-center gap-3">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
            <motion.div
              className={cn('relative h-full overflow-hidden rounded-full', TONE_CLASSES[tone].bar)}
              initial={false}
              animate={{ width: `${Math.max(4, Math.round((doneCount / items.length) * 100))}%` }}
              transition={{ type: 'spring', stiffness: 120, damping: 22 }}
            >
              {tone === 'working' && <span className="aimdoc-bar-shimmer absolute inset-0" />}
            </motion.div>
          </div>
          <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground">
            {doneCount}/{items.length}
          </span>
        </div>
      )}

      <AnimatePresence mode="wait" initial={false}>
        {items.length > 0 ? (
          <motion.div
            key="list"
            className="flex min-h-0 flex-1 flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <ProgressList items={items} />
          </motion.div>
        ) : (
          <motion.div
            key="empty"
            className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            {completed ? (
              <>
                <Check className="h-5 w-5 text-emerald-600" />
                <span className="text-sm text-muted-foreground">Complete</span>
              </>
            ) : failed ? (
              <>
                <AlertCircle className="h-5 w-5 text-red-600" />
                <span className="text-sm text-muted-foreground">Stopped</span>
              </>
            ) : tone === 'waiting' && question ? null : (
              <PlanSkeleton label={run ? 'Preparing the plan…' : 'Starting…'} />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {tone === 'waiting' && question && onAnswer && (
        <QuestionForm key={question.id} question={question} busy={answering} onAnswer={onAnswer} />
      )}
      {tone === 'waiting' && !question && (
        <div className="flex shrink-0 items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] px-3.5 py-2.5 text-xs text-amber-700 dark:text-amber-400">
          <MessageCircleQuestion className="mt-px h-3.5 w-3.5 shrink-0" />
          <span>Waiting for your answer. Loading the question…</span>
        </div>
      )}

      {failed && (
        <div className="flex shrink-0 items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/[0.06] px-3.5 py-2.5 text-xs text-red-600">
          <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 [overflow-wrap:anywhere]">{run?.error_message || 'The setup stopped. We have been notified and will pick it up.'}</span>
        </div>
      )}
    </Panel>
  );
}

export function BuildingStep({
  run,
  question = null,
  answering = false,
  onAnswer,
  mcpName,
  websiteHost,
  surfaceLabels,
  email,
}: {
  /** The onboarding job's setup wake, as GET /organization-onboarding summarizes it (plus live updates). */
  run: RunSummary | null;
  /** The job's open question for the owner, when it paused on one. */
  question?: AimdocJobQuestion | null;
  answering?: boolean;
  onAnswer?: (answer: BuildAnswer) => void | Promise<void>;
  mcpName: string | null;
  websiteHost: string;
  surfaceLabels: string[];
  email: string | null;
}) {
  const items = deriveProgress(run);
  const settled = isRunSettled(run);
  const failed = run?.status === 'failed' || run?.status === 'cancelled';
  const [celebrated, setCelebrated] = useState(false);
  useEffect(() => {
    if (settled && !failed) setCelebrated(true);
  }, [settled, failed]);
  const viewport = useViewportSize();

  return (
    <>
      {/* Portaled: the stage's motion wrappers carry filter/transform, which
          would otherwise make this fixed canvas position against them. */}
      {celebrated &&
        viewport.width > 0 &&
        createPortal(
          <Confetti
            width={viewport.width}
            height={viewport.height}
            recycle={false}
            numberOfPieces={260}
            gravity={0.2}
            colors={['#1173F0', '#3B8BFF', '#7DB4FF', '#10B981', '#0B0E14', '#F5B544']}
            style={{ position: 'fixed', inset: 0, zIndex: 60, pointerEvents: 'none' }}
          />,
          document.body
        )}
      <div className="flex min-h-0 flex-1 flex-col justify-center">
        {/* minmax(0,1fr) columns: the live status lines carry agent text
            (URLs, tool names, file paths), and an implicit auto column grows
            to its longest unbreakable word, widening the card mid-build. */}
        <div className="mx-auto grid max-h-[460px] min-h-0 w-full max-w-[460px] flex-1 grid-cols-[minmax(0,1fr)] grid-rows-[minmax(0,1fr)] gap-4">
          <Track
            title="Setting up your workspace"
            subtitle={[mcpName ? `From ${mcpName}` : null, websiteHost, surfaceLabels.join(' + ')].filter(Boolean).join(' · ')}
            run={run}
            items={items}
            question={question}
            answering={answering}
            onAnswer={onAnswer}
          />
        </div>
        <div className="mx-auto mt-4 flex w-full max-w-[460px] shrink-0 items-center gap-3 rounded-2xl border border-border/80 bg-card/80 px-4 py-3 text-[13px] text-muted-foreground backdrop-blur">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted/70">
            <Clock className="h-4 w-4" />
          </span>
          <span className="min-w-0 flex-1">
            Usually 3 to 5 minutes. You can leave this page; we'll email{' '}
            {email ? <span className="font-medium text-foreground">{email}</span> : 'you'} when it's ready to review.
          </span>
          <ElapsedTimer running={!settled} />
        </div>
        {/* Dogfooding, said out loud: the setup above is an Aimdoc job. */}
        <motion.p
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.4, ease: ONBOARDING_EASE }}
          className="mt-3 flex shrink-0 items-center justify-center gap-1.5 text-center text-xs leading-4 text-muted-foreground"
        >
          <AimdocSymbol className="h-3.5 w-3.5" aria-hidden />
          <span>
            Powered by <span className="font-semibold text-foreground">Aimdoc</span>
            {' — '}
            the setup above is an Aimdoc job working for you.
          </span>
        </motion.p>
      </div>
    </>
  );
}
